import { Table } from '@/components/ui/table'
import { formatDate } from '@/dates'
import type { FullRejectionListItem } from '@/features/host-review/types'
import { useTranslation } from '@/translations/i18n'

/**
 * The Pro Photo team's read-only window onto full rejections.
 *
 * Read-only by construction: there is no mutating operation in the contract and
 * none is offered here. The follow-up loop - outreach, and a reshoot-block
 * override where warranted - runs through the normal quote tooling.
 */
export const FullRejectionList = ({
	items,
	onSelect
}: {
	items: FullRejectionListItem[]
	onSelect: (reviewId: string) => void
}) => {
	const { t } = useTranslation()
	const unknown = t('host_review:full_rejections.unknown')

	if (items.length === 0) {
		return (
			<p className='p-6 text-neutral-600 text-sm/5'>
				{t('host_review:full_rejections.empty')}
			</p>
		)
	}

	return (
		<div className='overflow-x-auto'>
			<Table.Root className='min-w-3xl'>
				<Table.Header>
					<Table.HeaderRow>
						<Table.HeaderCell>
							<Table.HeaderCellLabel>
								{t('host_review:full_rejections.task')}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
						<Table.HeaderCell>
							<Table.HeaderCellLabel>
								{t('host_review:full_rejections.listing')}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
						<Table.HeaderCell>
							<Table.HeaderCellLabel>
								{t('host_review:full_rejections.photographer')}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
						<Table.HeaderCell>
							<Table.HeaderCellLabel>
								{t('host_review:full_rejections.closed_at')}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
						<Table.HeaderCell>
							<Table.HeaderCellLabel>
								{t('host_review:full_rejections.removed_count')}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
						<Table.HeaderCell>
							<Table.HeaderCellLabel>
								{t('host_review:full_rejections.reason')}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
					</Table.HeaderRow>
				</Table.Header>
				<Table.Body>
					{items.map((item) => (
						<Table.Row
							className='cursor-pointer'
							key={item.reviewId}
							onClick={() => onSelect(item.reviewId)}
						>
							<Table.Cell>
								<span className='truncate text-neutral-900 text-sm/5'>
									{item.shoot.taskIdentifier ??
										String(item.shoot.taskId)}
								</span>
							</Table.Cell>
							<Table.Cell>
								<span className='truncate text-neutral-700 text-sm/5'>
									{item.shoot.listingTitle ?? unknown}
								</span>
							</Table.Cell>
							<Table.Cell>
								<span className='truncate text-neutral-700 text-sm/5'>
									{item.shoot.photographerName ?? unknown}
								</span>
							</Table.Cell>
							<Table.Cell>
								<span className='text-neutral-700 text-sm/5'>
									{formatDate(item.closedAt, 'PP')}
								</span>
							</Table.Cell>
							<Table.Cell>
								<span className='text-neutral-700 text-sm/5'>
									{item.removedCount}
								</span>
							</Table.Cell>
							<Table.Cell>
								{/* The reason is shown verbatim - it is what the host
								    actually wrote, and paraphrasing it would lose the
								    detail the follow-up depends on. */}
								<span
									className='truncate text-neutral-700 text-sm/5'
									title={item.rejectionReason}
								>
									{item.rejectionReason}
								</span>
							</Table.Cell>
						</Table.Row>
					))}
				</Table.Body>
			</Table.Root>
		</div>
	)
}

FullRejectionList.displayName = 'FullRejectionList'
